import type { ListingsBlockProps, Listing } from '../types';
import { theme, sectionColors } from '../theme';

function formatPrice(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

/**
 * Pure listings grid. The renderer is "dumb": it receives `listings` from the host
 * (tenant-sites reads listings_cache server-side; builder hydrates from an API) and
 * NEVER fetches. Renders a graceful empty state rather than throwing.
 */
export function ListingsBlock({ props, listings }: { props: ListingsBlockProps; listings: Listing[] }) {
  const { heading, limit, layout = 'grid' } = props;
  const items = typeof limit === 'number' ? listings.slice(0, limit) : listings;
  const colors = sectionColors(props, { bg: 'transparent', text: theme.text });

  if (items.length === 0) {
    return (
      <section className="px-6 py-12 text-center" style={{ ...colors, fontFamily: theme.bodyFont }}>
        <p className="opacity-60">No listings available right now.</p>
      </section>
    );
  }

  const grid = layout === 'list' ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
  return (
    <section className="px-6 py-12" style={{ ...colors, fontFamily: theme.bodyFont }}>
      {heading ? <h2 className="mx-auto mb-8 max-w-6xl text-2xl font-semibold" style={{ fontFamily: theme.headingFont }}>{heading}</h2> : null}
      <div className={`mx-auto grid max-w-6xl gap-6 ${grid}`}>
        {items.map((l) => (
          // Each card links to the listing detail page (/listings/:key) so the
          // detail flow works end-to-end on the live site.
          <a
            key={l.listing_key}
            href={`/listings/${encodeURIComponent(l.listing_key)}`}
            className="tmpl-card group block overflow-hidden rounded-lg border border-black/10 transition hover:shadow-md"
          >
            <div className="tmpl-card-media relative overflow-hidden">
              {l.photos?.[0] ? (
                // Slow image zoom on hover — a premium real-estate touch that composes
                // with the per-look card styling (additive transform only).
                <img src={l.photos[0]} alt={l.address.street} loading="lazy" decoding="async" className="tmpl-card-img h-48 w-full object-cover transition-transform duration-500 ease-out will-change-transform group-hover:scale-105" />
              ) : null}
              {/* Hover-reveal overlay — shown only for the 'overlay' card style (CSS in the host app). */}
              <div className="tmpl-card-reveal hidden" aria-hidden="true">
                <span className="tmpl-card-reveal-status">For Sale</span>
                <ul className="tmpl-card-reveal-facts">
                  {l.beds != null ? <li>{l.beds} Bedrooms</li> : null}
                  {l.baths_total != null ? <li>{l.baths_total} Bathrooms</li> : null}
                  {l.sqft != null ? <li>{l.sqft.toLocaleString()} Sq Ft</li> : null}
                </ul>
                <span className="tmpl-card-reveal-cta">View Details</span>
              </div>
            </div>
            <div className="p-4">
              <div className="text-xl font-semibold tabular-nums">{formatPrice(l.list_price)}</div>
              <div className="text-sm">{l.address.street}</div>
              <div className="text-sm opacity-70">
                {l.address.city}, {l.address.state} {l.address.zip}
              </div>
              <div className="mt-2 text-sm opacity-70">
                {l.beds} bd · {l.baths_total} ba{l.sqft ? ` · ${l.sqft.toLocaleString()} sqft` : ''}
              </div>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}
